/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import {
  useProductShelfACESResults,
  useACESProductShelfResultsData,
} from '@/features/category/api/getProductShelfACESResults';
import { useHeaderData } from '@/features/header/api/getHeader';
import { getPreferredVehicle } from '@/features/header/utils/getPreferredVehicle';
import { extractVehicleQuestions } from '@/features/header/utils/getVehicleQuestions';
import { useSearchSkuRecordData } from '@/features/search/api/getSearchRecordData';
import { type AcesSearchSkuRecordResponse } from 'src/features/search/interface';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { validateAndFilterVehicleParams } from '@/features/ymme/utils/validateVehicleParams';
import { useLocale } from '@/hooks/useLocale';
import { useAppState } from '@/stores/AppState';
import { showXMPreviewDate } from '@/utils/showXMPreviewDate';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { type ProductShelfACESResponseModel } from '@/api/types/browse-search-types';
import {
  mapPartsProProductShelfResultsAttributes,
  mapProductSpecificationAttributes,
} from '../utils/mapProductShelfResultsAttributes';

export const useCMSProductFinderAttributes = () => {
  const router = useLegacyRouter();
  const isSearchPage = router.route === '/searchresult';
  const headerResult = useHeaderData();
  const { acesEnabled } = useAcesFlags();
  const preferredVehicle = getPreferredVehicle(headerResult.data);
  const locale = useLocale();
  const appState = useAppState();

  // Shelf page data sources
  const {
    data: acesData,
    isPending,
    isFetching,
  } = useProductShelfACESResults({
    enabled: !isSearchPage && acesEnabled,
  });
  const isShelfACESProductFinderPending = !isSearchPage && acesEnabled && (isPending || isFetching);

  // Search page: fetch shelf ACES results using the taxonomy from the first search SKU
  const { data: searchSkuRecordData } = useSearchSkuRecordData();
  const acesSkuRecord = isSearchPage
    ? (searchSkuRecordData as AcesSearchSkuRecordResponse | undefined)?.searchResults?.skuRecord
    : undefined;
  const searchTaxonomyPath = acesSkuRecord?.taxonomyPath;
  const searchPartGroupId = acesSkuRecord?.partGroupId ?? '';

  const vehicleQuestions = extractVehicleQuestions(
    headerResult.data?.vehicleMap?.acesVehicleAttributes?.vehicleQuestionsIds,
    true,
  ) as string;

  const validatedVehicleParams = validateAndFilterVehicleParams({
    year: preferredVehicle?.year,
    makeId: preferredVehicle?.makeId,
    modelId: preferredVehicle?.modelId,
    vehicleTypeId: preferredVehicle?.vehicleTypeId,
    vehicleQuestions: vehicleQuestions || undefined,
  });

  const searchShelfQueryResult = useACESProductShelfResultsData({
    queryOptions: {
      locale,
      partGroupId: searchPartGroupId,
      taxonomyPath: searchTaxonomyPath,
      canonicalPath: searchTaxonomyPath ?? '',
      pageType: 'ProductShelf',
      path: searchTaxonomyPath ?? '',
      recsPerPage: '24',
      storeId: headerResult.data?.storeNumber ?? undefined,
      preview: showXMPreviewDate(),
      rewardsId: headerResult.data?.myAccountMap?.rewardsId ?? '',
      eCookieId: appState.state.eCookieId,
      ...validatedVehicleParams,
    },
    enabled: isSearchPage && acesEnabled && !!searchTaxonomyPath && !!searchPartGroupId,
    staleTime: 30 * 60000,
    retryOnMount: false,
  });
  const searchShelfPage0 = searchShelfQueryResult.data?.pages[0];
  const isSearchACESProductFinderPending =
    isSearchPage &&
    acesEnabled &&
    Boolean(searchTaxonomyPath && searchPartGroupId) &&
    searchShelfQueryResult.isPending;

  const data = isSearchPage ? undefined : acesData;

  const attributes = mapPartsProProductShelfResultsAttributes(data, acesEnabled);

  const specifications = isSearchPage
    ? searchShelfPage0?.productShelfResults?.specifications
    : (data as ProductShelfACESResponseModel)?.productShelfResults?.specifications;

  const acesAttributes = mapProductSpecificationAttributes(specifications);

  const totalNumberOfRecords = isSearchPage
    ? searchShelfPage0?.productShelfResults?.totalNumberOfRecords
    : data?.productShelfResults?.totalNumberOfRecords;

  return {
    specificationsAttributes: acesEnabled ? acesAttributes : attributes,
    totalNumberOfRecords,
    isLoading:
      acesEnabled &&
      (isSearchPage ? isSearchACESProductFinderPending : isShelfACESProductFinderPending),
  };
};
